import type { OfficePositionAvatarData } from "@bezoned/desktop-bridge/office-position-messaging.js";

import type { FluidSharedTree } from "../../fluid/fluid.client.js";
import { getUser } from "../../fluid/fluid.service.js";

export const getOnlineOccupantIds = ({
  occupantIds,
  sharedTree,
  alwaysIncludeUserIds = [],
}: {
  occupantIds: string[];
  sharedTree: FluidSharedTree;
  alwaysIncludeUserIds?: string[];
}): string[] => {
  const onlineOccupants: string[] = [];
  const seen = new Set<string>();
  const alwaysInclude = new Set(alwaysIncludeUserIds);

  for (const userId of occupantIds) {
    if (seen.has(userId)) {
      continue;
    }
    seen.add(userId);

    if (alwaysInclude.has(userId)) {
      onlineOccupants.push(userId);
      continue;
    }

    const fluidUser = getUser({ sharedTree, userId });
    if (!fluidUser?.isOnline) {
      continue;
    }

    onlineOccupants.push(userId);
  }

  return onlineOccupants;
};

const resolveDisplayName = (
  userId: string,
  selfUserId: string,
  selfName: string,
  sharedTree: FluidSharedTree
): string => {
  if (userId === selfUserId) {
    return selfName;
  }

  const fluidUser = getUser({ sharedTree, userId });
  return fluidUser?.name ?? userId;
};

const resolvePresence = (
  userId: string,
  sharedTree: FluidSharedTree
): { availability?: string; outOfOffice?: boolean } | undefined => {
  const fluidUser = getUser({ sharedTree, userId });
  if (!fluidUser?.presence) {
    return;
  }

  return {
    availability: fluidUser.presence.availability,
    outOfOffice: fluidUser.presence.outOfOffice?.isOutOfOffice ?? false,
  };
};

export const buildPositionSnapshot = ({
  occupantIds,
  selfUserId,
  selfName,
  sharedTree,
  pictureUrlByUserId,
}: {
  occupantIds: string[];
  selfUserId: string;
  selfName: string;
  sharedTree: FluidSharedTree;
  pictureUrlByUserId: Map<string, string>;
}): OfficePositionAvatarData[] => {
  const avatars: OfficePositionAvatarData[] = [];

  for (const userId of getOnlineOccupantIds({
    alwaysIncludeUserIds: [selfUserId],
    occupantIds,
    sharedTree,
  })) {
    const isSelf = userId === selfUserId;
    const displayName = resolveDisplayName(
      userId,
      selfUserId,
      selfName,
      sharedTree
    );
    const pictureUrl = pictureUrlByUserId.get(userId);
    const presence = resolvePresence(userId, sharedTree);

    avatars.push({
      displayName,
      isSelf,
      userId,
      ...(pictureUrl ? { pictureUrl } : {}),
      ...(presence ? { presence } : {}),
    });
  }

  return avatars;
};
